import { Injectable } from '@angular/core';
import { Category } from '@vendasta/google-analytics';
import { endOfDay, startOfDay } from 'date-fns';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { alignLocalDateToUTC, getDateBoundaries } from '../../../core/metrics.helper';
import {
  Alignment,
  Calendar,
  CompositeFilter_Operator,
  FieldFilter_Operator,
  Operator,
  Type,
} from '../../../core/multi_location_analytics_sdk/_internal/enums';
import {
  Aggregate,
  AlignmentPeriod,
  DateRange,
  Measure,
  QueryMetricsRequest,
  QueryMetricsResponse,
} from '../../../core/multi_location_analytics_sdk/_internal/objects';
import { MultiLocationAnalyticsService } from '../../../core/multi_location_analytics_sdk/multi-location-analytics.service';
import { Site, SiteService } from '../../../core/site';
import { Ga4RecordMap } from '../../ga4-metrics/ga4-metrics.interface';
import { ChartEntry } from '../../metrics';

@Injectable()
export class Ga4OverviewMetricsService {
  loadingGA4Records$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  loadingGA4Records$: Observable<boolean> = this.loadingGA4Records$$.asObservable();
  loadingGA4RecordsError$$: BehaviorSubject<boolean> = new BehaviorSubject<any>(false);
  loadingGA4RecordsError$: Observable<boolean> = this.loadingGA4RecordsError$$.asObservable();
  ga4Records$$: BehaviorSubject<[Ga4RecordMap]> = new BehaviorSubject<[Ga4RecordMap]>(undefined);
  ga4Records$: Observable<[Ga4RecordMap]> = this.ga4Records$$.asObservable();

  site$: Observable<Site>;

  constructor(private multiLocationAnalyticsService: MultiLocationAnalyticsService, private siteService: SiteService) {
    // this week VS last week
    const [start, end] = getDateBoundaries('7days');

    this.site$ = this.siteService.site$;
    this.site$
      .pipe(
        tap(() => {
          this.loadingGA4Records$$.next(true);
        }),
        switchMap((site: Site) => {
          return combineLatest([
            this.queryAndParseGARecord(site.partnerId, site.accountId, startOfDay(start), endOfDay(end)),
          ]);
        }),
        catchError(() => {
          this.loadingGA4RecordsError$$.next(true);
          this.loadingGA4Records$$.next(false);
          return of(null);
        }),
      )
      .subscribe((response) => {
        this.ga4Records$$.next(response);
        this.loadingGA4Records$$.next(false);
      });
  }

  queryAndParseGARecord(partnerId: string, accountId: string, start: Date, end: Date): Observable<Ga4RecordMap> {
    return this.multiLocationAnalyticsService
      .queryMetrics(this.getGA4RecordsRequest(partnerId, accountId, start, end))
      .pipe(
        map((resp: QueryMetricsResponse) => {
          const metrics = resp.metricResults[0].metrics.metrics;
          if (!metrics) {
            return {};
          }
          const recordDataForActiveUser: ChartEntry[] = [];
          const recordDataForSessionDuration: ChartEntry[] = [];
          metrics.map((m) => {
            recordDataForActiveUser.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[0],
            } as ChartEntry);
            recordDataForSessionDuration.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[1],
            } as ChartEntry);
          });
          return {
            activeUsers: recordDataForActiveUser,
            averageSession: recordDataForSessionDuration,
          } as Ga4RecordMap;
        }),
        shareReplay(1),
      );
  }

  getGA4RecordsRequest(partnerId: string, accountId: string, startDate: Date, endDate: Date): QueryMetricsRequest {
    const req: QueryMetricsRequest = this.getBaseRequest(partnerId, accountId);
    req.alignmentPeriod = new AlignmentPeriod({
      calendar: Calendar.CALENDAR_YEAR,
    });
    req.alignment = Alignment.ALIGN_DELTA;
    req.dateRange = new DateRange({
      start: startDate,
      end: endDate,
    });
    req.measures = [
      new Measure({
        aggregate: new Aggregate({
          measure: 'active_users',
          aggOp: Operator.SUM,
        }),
      }),
      new Measure({
        aggregate: new Aggregate({
          measure: 'session_duration',
          aggOp: Operator.AVG,
        }),
      }),
    ];
    return req;
  }

  getBaseRequest(partnerId: string, accountId: string): QueryMetricsRequest {
    return new QueryMetricsRequest({
      partnerId: partnerId,
      metricName: 'google_analytics_GA4_record',
      resourceIds: [
        {
          businessId: { businessId: accountId },
        },
      ],
      filter: {
        compositeFilter: {
          op: CompositeFilter_Operator.AND,
          filters: [
            {
              fieldFilter: {
                dimension: 'category',
                operator: FieldFilter_Operator.EQUAL,
                value: {
                  valueType: Type.PROPERTY_TYPE_STRING,
                  value: Category[Category.CATEGORY_WEBSITE],
                },
              },
            },
            {
              fieldFilter: {
                dimension: 'subcategory',
                operator: FieldFilter_Operator.EQUAL,
                value: {
                  valueType: Type.PROPERTY_TYPE_STRING,
                  value: Category[Category.CATEGORY_WEBSITE],
                },
              },
            },
          ],
        },
      },
    });
  }
}
